// src/scenes/game-components/UIManager.js
// 🎨 UIManager - UPDATED: Removed lives display for wedding party game
// Manages scoreboard, status displays, and UI updates

import { gameState } from '../../game/state.js';
import { GAME_CONFIG } from '../../game/config.js';

export default class UIManager {
  constructor(scene) {
    this.scene = scene;
    this.ui = {};
  }

  createUI() {
    const { width } = this.scene.scale;
    
    // Enhanced vibrant UI background with gradients
    const uiContainer = this.scene.add.container(0, 0);
    
    // Main UI background with colorful gradient
    const uiBg = this.scene.add.graphics();
    uiBg.fillGradientStyle(0xFF69B4, 0xFF69B4, 0x4ECDC4, 0x4ECDC4, 1);
    uiBg.fillRoundedRect(10, 10, 350, 100, 8);
    uiBg.lineStyle(4, 0xFFD700);
    uiBg.strokeRoundedRect(10, 10, 350, 100, 8);
    uiBg.setScrollFactor(0);
    
    // Inner panel with 3D effect
    const innerBg = this.scene.add.graphics();
    innerBg.fillStyle(0xE0E0E0);
    innerBg.fillRoundedRect(15, 15, 340, 90, 4);
    innerBg.lineStyle(2, 0x808080);
    innerBg.strokeRoundedRect(15, 15, 340, 90, 4);
    innerBg.setScrollFactor(0);
    
    // Enhanced Score with larger font and colors
    this.ui.scoreLabel = this.scene.add.text(25, 25, 'SCORE:', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 2
    }).setScrollFactor(0);
    
    this.ui.scoreText = this.scene.add.text(120, 25, gameState.score.toString(), {
      fontSize: '16px',
      fontFamily: 'Arial, sans-serif',
      color: '#FF1493',
      stroke: '#000000',
      strokeThickness: 2
    }).setScrollFactor(0);
    
    // Level with enhanced styling
    this.ui.levelText = this.scene.add.text(25, 45, `LEVEL: ${gameState.level}`, {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#4ECDC4',
      stroke: '#000000',
      strokeThickness: 2
    }).setScrollFactor(0);
    
    // Player name with character emoji
    const characterEmoji = gameState.character === 'BRIDE' ? '👰' : '🤵';
    this.ui.nameText = this.scene.add.text(25, 65, `${characterEmoji} ${gameState.playerName}`, {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#2C3E50',
      stroke: '#FFFFFF',
      strokeThickness: 1
    }).setScrollFactor(0);
    
    // Items with progress bar
    this.ui.itemsText = this.scene.add.text(25, 80, `ITEMS: ${gameState.itemsCollected}/${GAME_CONFIG.LEVELS.ITEMS_PER_LEVEL}`, {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#32CD32',
      stroke: '#000000',
      strokeThickness: 1
    }).setScrollFactor(0);
    
    // Time with urgent coloring
    this.ui.timeText = this.scene.add.text(width - 150, 25, `TIME: ${gameState.getFormattedTime()}`, {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#32CD32',
      stroke: '#000000',
      strokeThickness: 2
    }).setScrollFactor(0);
    
    // Ability status (moved up to fill space where lives were)
    this.ui.abilityText = this.scene.add.text(width - 150, 45, '✨ READY', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 1
    }).setScrollFactor(0);
    
    // Current combo display (new addition)
    this.ui.comboText = this.scene.add.text(width - 150, 65, 'COMBO: 0', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#FF69B4',
      stroke: '#000000',
      strokeThickness: 1
    }).setScrollFactor(0);
    
    /* console.log('🎨 Enhanced UI created (no lives)'); */
  }

  update(time, delta) {
    this.updateScoreDisplay();
    this.updateTimeDisplay();
    this.updateAbilityStatus();
    this.updateItemsDisplay();
    this.updateComboDisplay();
  }

  updateScoreDisplay() {
    if (this.ui.scoreText) {
      // Update score with integer formatting
      this.ui.scoreText.setText(Math.floor(gameState.score).toString());
    }
  }

  updateTimeDisplay() {
    if (this.ui.timeText) {
      // Enhanced time display with color changes
      const timeRemaining = gameState.timeRemaining;
      const timeText = gameState.getFormattedTime();
      
      if (timeRemaining < 15) {
        this.ui.timeText.setStyle({ color: '#FF0000' }); // Red when very low
      } else if (timeRemaining < 30) {
        this.ui.timeText.setStyle({ color: '#FFA500' }); // Orange when low
      } else {
        this.ui.timeText.setStyle({ color: '#32CD32' }); // Green when good
      }
      
      this.ui.timeText.setText(`TIME: ${timeText}`);
    }
  }

  updateAbilityStatus() {
    if (this.ui.abilityText) {
      // Update ability status
      if (gameState.abilityReady) {
        this.ui.abilityText.setText('✨ READY').setStyle({ color: '#FFD700' });
      } else {
        this.ui.abilityText.setText('⏳ COOLDOWN').setStyle({ color: '#808080' });
      }
    }
  }

  updateItemsDisplay() {
    if (this.ui.itemsText) {
      const collected = gameState.itemsCollected;
      const total = GAME_CONFIG.LEVELS.ITEMS_PER_LEVEL;
      
      // Color coding for progress
      let color = '#32CD32'; // Green
      if (collected < total * 0.3) {
        color = '#FF6347'; // Red for low progress
      } else if (collected < total * 0.7) {
        color = '#FFA500'; // Orange for medium progress
      }
      
      this.ui.itemsText.setText(`ITEMS: ${collected}/${total}`);
      this.ui.itemsText.setStyle({ color: color });
    }
  }

  updateComboDisplay() {
    if (this.ui.comboText) {
      const combo = gameState.stats.currentCombo;
      
      // Color coding for combo
      let color = '#FF69B4'; // Default pink
      if (combo > 10) {
        color = '#FFD700'; // Gold for high combo
      } else if (combo > 5) {
        color = '#FFA500'; // Orange for medium combo
      }
      
      this.ui.comboText.setText(`COMBO: ${combo}`);
      this.ui.comboText.setStyle({ color: color });
    }
  }

  // Show temporary messages
  showMessage(text, x, y, duration = 2000, style = {}) {
    const defaultStyle = {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 2
    };
    
    const messageStyle = { ...defaultStyle, ...style };
    const message = this.scene.add.text(x, y, text, messageStyle).setOrigin(0.5);
    
    this.scene.tweens.add({
      targets: message,
      y: message.y - 30,
      alpha: 0,
      duration: duration,
      onComplete: () => {
        if (message && message.active) {
          message.destroy();
        }
      }
    });
    
    return message;
  }

  // Show points gained/lost
  showPointsChange(x, y, points, isGain = true) {
    const text = isGain ? `+${points}` : `-${points}`;
    const color = isGain ? '#32CD32' : '#FF0000';
    
    return this.showMessage(text, x, y, 1500, { 
      color: color,
      fontSize: '16px'
    });
  }

  // Show level completion bonus
  showLevelBonus(bonus) {
    const { width, height } = this.scene.scale;
    
    return this.showMessage(`LEVEL BONUS: +${bonus}!`, width/2, height/2, 3000, {
      fontSize: '20px',
      color: '#FFD700'
    });
  }

  // Show achievement unlocked
  showAchievement(achievementName) {
    const { width } = this.scene.scale;
    
    return this.showMessage(`🏆 ${achievementName.toUpperCase()} UNLOCKED!`, width/2, 150, 4000, {
      fontSize: '12px',
      color: '#FFD700'
    });
  }

  // Show combo milestone
  showComboMilestone(combo) {
    const { width, height } = this.scene.scale;
    
    let message = '';
    if (combo === 5) {
      message = '🔥 COMBO x5!';
    } else if (combo === 10) {
      message = '🌟 COMBO x10!';
    } else if (combo === 15) {
      message = '💫 AMAZING COMBO!';
    } else if (combo === 20) {
      message = '🎆 INCREDIBLE COMBO!';
    }
    
    if (message) {
      return this.showMessage(message, width/2, height * 0.3, 2000, {
        fontSize: '14px',
        color: '#FFD700'
      });
    }
  }

  // Show time warning
  showTimeWarning() {
    const { width, height } = this.scene.scale;
    
    return this.showMessage('⏰ TIME RUNNING OUT!', width/2, height * 0.4, 2000, {
      fontSize: '16px',
      color: '#FF0000'
    });
  }
}